import {
  DASHBOARD_SCHEMA_VERSION,
  type DashboardFilters,
  type DashboardSegment,
  type DashboardSnapshot,
  type DashboardSummary,
  type SegmentSort,
} from './types';

const SORT_KEYS: SegmentSort[] = ['revenue', 'lifetimeLtv', 'patients', 'conversion', 'ltvGrowth'];

export function isDashboardSnapshot(value: unknown): value is DashboardSnapshot {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<DashboardSnapshot>;
  if (item.schemaVersion !== DASHBOARD_SCHEMA_VERSION) return false;
  if (!item.summary || !Array.isArray(item.segments) || !item.source || !item.freshness) return false;
  return item.segments.every((segment) => {
    if (!segment || typeof segment !== 'object') return false;
    const row = segment as Partial<DashboardSegment>;
    return typeof row.id === 'string'
      && typeof row.medium === 'string'
      && typeof row.source === 'string'
      && typeof row.campaign === 'string'
      && ['leads', 'patients', 'conversion', 'revenue', 'ltv30', 'ltv90', 'ltv180', 'ltv365', 'lifetimeLtv', 'ltvGrowth', 'repeatRevenue']
        .every((key) => Number.isFinite(row[key as keyof DashboardSegment]));
  });
}

export function defaultFilters(search = ''): DashboardFilters {
  const params = new URLSearchParams(search);
  const sortValue = params.get('sort') as SegmentSort | null;
  return {
    medium: params.get('medium') || 'all',
    source: params.get('source') || 'all',
    query: params.get('q') || '',
    sort: sortValue && SORT_KEYS.includes(sortValue) ? sortValue : 'revenue',
  };
}

export function filtersToSearch(filters: DashboardFilters): string {
  const params = new URLSearchParams();
  if (filters.medium !== 'all') params.set('medium', filters.medium);
  if (filters.source !== 'all') params.set('source', filters.source);
  if (filters.query.trim()) params.set('q', filters.query.trim());
  if (filters.sort !== 'revenue') params.set('sort', filters.sort);
  const output = params.toString();
  return output ? `?${output}` : '';
}

export function filterSegments(segments: DashboardSegment[], filters: DashboardFilters): DashboardSegment[] {
  const query = filters.query.trim().toLocaleLowerCase('uk-UA');
  return segments
    .filter((segment) => filters.medium === 'all' || segment.medium === filters.medium)
    .filter((segment) => filters.source === 'all' || segment.source === filters.source)
    .filter((segment) => !query || [segment.medium, segment.source, segment.campaign]
      .some((value) => value.toLocaleLowerCase('uk-UA').includes(query)))
    .sort((a, b) => b[filters.sort] - a[filters.sort] || b.revenue - a.revenue || a.campaign.localeCompare(b.campaign));
}

export function summarizeSegments(segments: DashboardSegment[], globalSummary: DashboardSummary): DashboardSummary {
  const leads = segments.reduce((sum, row) => sum + row.leads, 0);
  const patients = segments.reduce((sum, row) => sum + row.patients, 0);
  const revenue = segments.reduce((sum, row) => sum + row.revenue, 0);
  const repeatRevenue = segments.reduce((sum, row) => sum + row.repeatRevenue, 0);
  const isGlobal = leads === globalSummary.leads && patients === globalSummary.patients && revenue === globalSummary.revenue;

  return {
    leads,
    patients,
    conversion: leads ? patients / leads : 0,
    revenue,
    lifetimeLtv: patients ? revenue / patients : 0,
    returningPatients: isGlobal ? globalSummary.returningPatients : 0,
    returningRate: isGlobal ? globalSummary.returningRate : 0,
    repeatRevenue,
    campaignsWithRevenue: segments.filter((row) => row.revenue > 0).length,
    campaignsWithRepeatRevenue: segments.filter((row) => row.repeatRevenue > 0).length,
  };
}

export function segmentStatus(segment: DashboardSegment): 'growth' | 'revenue' | 'no-patients' | 'zero-value' {
  if (!segment.patients) return 'no-patients';
  if (segment.ltvGrowth > 0) return 'growth';
  if (segment.revenue > 0) return 'revenue';
  return 'zero-value';
}

export function createInsight(segments: DashboardSegment[], summary: DashboardSummary): string {
  const growers = segments.filter((row) => row.ltvGrowth > 0).sort((a, b) => b.repeatRevenue - a.repeatRevenue);
  const unattributed = segments.find((row) => row.medium === '(не передано)' && row.source === '(не передано)');
  const growthText = growers.length
    ? `${growers.length} ${pluralize(growers.length, 'сегмент', 'сегменти', 'сегментів')} генерують повторну виручку: ${growers.slice(0, 2).map((row) => `${displayCampaign(row)} (+${formatMoney(row.ltvGrowth)} до LTV-365)`).join(' та ')}.`
    : 'У поточному зрізі не зафіксовано зростання LTV після перших 30 днів.';
  const unattributedShare = unattributed && summary.revenue ? unattributed.revenue / summary.revenue : 0;
  const caveat = unattributedShare > 0.25
    ? ` ${formatPercent(unattributedShare)} виручки не має рекламної атрибуції — це головна прогалина даних.`
    : '';
  return `${growthText}${caveat}`;
}

export function displayCampaign(segment: DashboardSegment): string {
  if (segment.campaign === '(не передано)') {
    if (segment.source === '(не передано)') return 'Без атрибуції';
    return `${segment.medium} / ${segment.source}`;
  }
  return segment.campaign;
}

export function formatMoney(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('uk-UA', { maximumFractionDigits }).format(Math.round(value));
}

export function formatPercent(value: number): string {
  return new Intl.NumberFormat('uk-UA', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

export function pluralize(value: number, one: string, few: string, many: string): string {
  const lastTwo = value % 100;
  const last = value % 10;
  if (last === 1 && lastTwo !== 11) return one;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return few;
  return many;
}

export function freshnessFromDate(isoDate: string, now = new Date()): 'live' | 'stale' {
  const timestamp = Date.parse(isoDate);
  if (!Number.isFinite(timestamp)) return 'stale';
  return now.getTime() - timestamp <= 36 * 60 * 60 * 1000 ? 'live' : 'stale';
}
